import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  ShieldCheck,
  Database,
  Loader2,
  Building2,
  Users,
  Check,
  Info,
} from "lucide-react";
import Select from "react-select";
import {
  createUserRole,
  updateUserRole,
  getUserDataScopes,
  updateUserDataScopes,
} from "../../../api/settings/user_roles";
import { assignUserAccess } from "../../../api/settings/userAccess";
import { getUsageRoleOptions } from "../../../api/settings/usageRoles";
import { getEmployeesFilter } from "../../../api/hrm/employees/employeeInfo";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import axiosInstance from "../../../api/axiosInstance";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { SCOPE_LEVEL_OPTIONS } from "../../../config/featureGroups";

const createEmptyScopeDraft = () => ({
  all: false,
  self: false,
  team: false,
  branches: [],
  departments: [],
});

const buildScopeDrafts = (modules = [], scopes = []) => {
  const drafts = {};
  modules.forEach((moduleItem) => {
    drafts[moduleItem.code] = createEmptyScopeDraft();
  });

  scopes.forEach((scope) => {
    const moduleCode = scope.module_code;
    if (!drafts[moduleCode]) {
      drafts[moduleCode] = createEmptyScopeDraft();
    }

    const type = scope.scope_type === "global" ? "all" : scope.scope_type;
    if (type === "all" || type === "self" || type === "team") {
      drafts[moduleCode][type] = true;
    }
    if (type === "branch" && scope.scope_value) {
      drafts[moduleCode].branches = [
        ...new Set([...drafts[moduleCode].branches, String(scope.scope_value)]),
      ];
    }
    if (type === "department" && scope.scope_value) {
      drafts[moduleCode].departments = [
        ...new Set([
          ...drafts[moduleCode].departments,
          String(scope.scope_value),
        ]),
      ];
    }
  });

  return drafts;
};

const summarizeScope = (draft) => {
  if (!draft) return "Chưa cấu hình";
  if (draft.all) return "Toàn bộ";
  const parts = [];
  if (draft.self) parts.push("Tự tạo");
  if (draft.team) parts.push("Đội nhóm");
  if (draft.branches.length) parts.push(`${draft.branches.length} CN`);
  if (draft.departments.length) parts.push(`${draft.departments.length} PB`);
  return parts.length ? parts.join(" • ") : "Chưa cấu hình";
};

const scopeLabel = (level) =>
  SCOPE_LEVEL_OPTIONS.find((o) => o.value === level)?.label || level || "—";

const formatEmployeeLabel = (e) => {
  const name = e.employee_name || e.name || "";
  return [name, e.department_name, e.code].filter(Boolean).join(" • ");
};

const AssignDrawer = ({ isOpen, onClose, onSaved, data }) => {
  const [branchId, setBranchId] = useState("");
  const [form, setForm] = useState({ employee_id: "", role_id: "" });
  const [usageRoleId, setUsageRoleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeesRaw, setEmployeesRaw] = useState([]);
  const [usageRoles, setUsageRoles] = useState([]);
  const [usageRolesLoading, setUsageRolesLoading] = useState(false);
  const [currentRoles, setCurrentRoles] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("role");
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeModules, setScopeModules] = useState([]);
  const [scopeDrafts, setScopeDrafts] = useState({});
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const isEditing = !!data;

  const selectedEmployee = useMemo(
    () => employeesRaw.find((e) => e.id === form.employee_id) || null,
    [employeesRaw, form.employee_id]
  );

  // Load chi nhánh khi mở
  useEffect(() => {
    if (!isOpen) return;
    getAllBranches_Array({ is_active: true })
      .then((rows) =>
        setBranchOptions(
          (rows || []).map((b) => ({ value: b.id, label: b.name }))
        )
      )
      .catch(() => setBranchOptions([]));
  }, [isOpen]);

  // Reset / prefill khi mở hoặc edit
  useEffect(() => {
    if (!isOpen) return;
    setActiveTab("role");
    setUsageRoleId("");
    setCurrentRoles([]);
    setUsageRoles([]);

    if (data) {
      setForm({ employee_id: data.employee_id, role_id: data.role_id });
      setBranchId(data.branch_id || "");
      setUsageRoleId(data.usage_role_id || "");
      // Load NV của CN (hoặc tất cả active nếu chưa có CN trên row)
      (async () => {
        try {
          const rows = await getEmployeesFilter({
            branch_id: data.branch_id || undefined,
            is_active: "true",
          });
          const list = Array.isArray(rows) ? rows : [];
          setEmployeesRaw(list);
          setEmployeeOptions(
            list.map((e) => ({ value: e.id, label: formatEmployeeLabel(e) }))
          );
          if (!data.branch_id) {
            const emp = list.find((e) => e.id === data.employee_id);
            if (emp?.branch_id) setBranchId(emp.branch_id);
          }
        } catch {
          setEmployeesRaw([]);
          setEmployeeOptions([]);
        }
      })();
    } else {
      setForm({ employee_id: "", role_id: "" });
      setBranchId("");
      setEmployeesRaw([]);
      setEmployeeOptions([]);
    }
  }, [isOpen, data]);

  // Đổi chi nhánh → load NV active của CN
  useEffect(() => {
    if (!isOpen || !branchId || isEditing) return;

    let cancelled = false;
    (async () => {
      try {
        const rows = await getEmployeesFilter({
          branch_id: branchId,
          is_active: "true",
        });
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setEmployeesRaw(list);
        setEmployeeOptions(
          list.map((e) => ({ value: e.id, label: formatEmployeeLabel(e) }))
        );
        setForm({ employee_id: "", role_id: "" });
        setUsageRoleId("");
        setCurrentRoles([]);
        setUsageRoles([]);
      } catch {
        if (!cancelled) {
          setEmployeesRaw([]);
          setEmployeeOptions([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, branchId, isEditing]);

  // Chọn NV → load quyền hiện có + options quyền theo CN/BP
  useEffect(() => {
    const empId = form.employee_id;
    if (!isOpen || !empId) {
      if (!isEditing) setUsageRoles([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setRoleLoading(true);
        const res = await axiosInstance.get(`/settings/user_roles/by-employee/${empId}`);
        if (!cancelled) setCurrentRoles(res.data || []);
      } catch {
        if (!cancelled) {
          setCurrentRoles([]);
          showErrorToast("Không thể tải quyền hiện tại của nhân viên");
        }
      } finally {
        if (!cancelled) setRoleLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, form.employee_id, isEditing]);

  useEffect(() => {
    const emp = selectedEmployee;
    const bId = branchId || emp?.branch_id;
    if (!isOpen || !bId || !form.employee_id) {
      if (!form.employee_id) setUsageRoles([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setUsageRolesLoading(true);
        const params = { branch_id: bId };
        if (emp?.department_id) params.department_id = emp.department_id;
        const rows = await getUsageRoleOptions(params);
        if (cancelled) return;
        setUsageRoles(Array.isArray(rows) ? rows : []);

        // Prefill selection khi edit / đã có quyền
        if (form.role_id) {
          const match = (rows || []).find((r) => r.role_id === form.role_id);
          if (match) setUsageRoleId(match.id);
        }
      } catch {
        if (!cancelled) setUsageRoles([]);
      } finally {
        if (!cancelled) setUsageRolesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    branchId,
    form.employee_id,
    form.role_id,
    selectedEmployee?.branch_id,
    selectedEmployee?.department_id,
  ]);

  useEffect(() => {
    const loadScopes = async () => {
      const employeeId = data?.employee_id || form.employee_id;
      if (!isOpen || !employeeId) {
        setScopeModules([]);
        setScopeDrafts({});
        return;
      }

      try {
        setScopeLoading(true);
        const res = await getUserDataScopes(employeeId);
        setScopeModules(res.modules || []);
        setScopeDrafts(buildScopeDrafts(res.modules || [], res.scopes || []));
        setBranches(res.branches || []);
        setDepartments(res.departments || []);
      } catch (err) {
        console.error("Lỗi load user data scopes:", err);
        showErrorToast("Không thể tải phạm vi dữ liệu override.");
      } finally {
        setScopeLoading(false);
      }
    };

    loadScopes();
  }, [isOpen, data, form.employee_id]);

  const handleSelectEmployee = (opt) => {
    const empId = opt?.value || "";
    setForm({ employee_id: empId, role_id: "" });
    setUsageRoleId("");
    setCurrentRoles([]);
    setUsageRoles([]);
  };

  const handleSelectUsageRole = (ur) => {
    setUsageRoleId(ur.id);
    setForm((prev) => ({ ...prev, role_id: ur.role_id }));
  };

  const handleSave = async () => {
    if (!form.employee_id || !form.role_id) {
      showErrorToast("Vui lòng chọn nhân viên và quyền sử dụng");
      return;
    }
    try {
      setLoading(true);
      if (usageRoleId) {
        // Gán theo quyền sử dụng (thay thế role hiện tại)
        await assignUserAccess(form.employee_id, { usage_role_id: usageRoleId });
      } else if (isEditing) {
        await updateUserRole(undefined, {
          old_employee_id: data.employee_id,
          old_role_id: data.role_id,
          ...form,
        });
      } else {
        await createUserRole(form);
      }
      showSuccessToast("Đã gán quyền sử dụng cho nhân viên");
      onSaved();
      onClose();
    } catch (err) {
      console.error("Lỗi khi lưu phân quyền:", err);
      showErrorToast(err?.response?.data?.error || "Không thể lưu phân quyền");
    } finally {
      setLoading(false);
    }
  };

  const updateScopeDraft = (moduleCode, updater) => {
    setScopeDrafts((prev) => {
      const current = prev[moduleCode] || createEmptyScopeDraft();
      return {
        ...prev,
        [moduleCode]: updater(current),
      };
    });
  };

  const toggleScopeFlag = (moduleCode, flag) => {
    updateScopeDraft(moduleCode, (current) => {
      const nextValue = !current[flag];
      if (flag === "all") {
        return nextValue
          ? { ...createEmptyScopeDraft(), all: true }
          : createEmptyScopeDraft();
      }
      return {
        ...current,
        all: false,
        [flag]: nextValue,
      };
    });
  };

  const updateScopeMultiValue = (moduleCode, key, items) => {
    updateScopeDraft(moduleCode, (current) => ({
      ...current,
      all: false,
      [key]: items.map((item) => item.value),
    }));
  };

  const buildScopePayload = () => {
    const payload = [];

    Object.entries(scopeDrafts).forEach(([moduleCode, draft]) => {
      if (draft.all) {
        payload.push({ module_code: moduleCode, scope_type: "all", scope_value: null });
        return;
      }
      if (draft.self) {
        payload.push({ module_code: moduleCode, scope_type: "self", scope_value: null });
      }
      if (draft.team) {
        payload.push({ module_code: moduleCode, scope_type: "team", scope_value: null });
      }
      draft.branches.forEach((value) => {
        payload.push({ module_code: moduleCode, scope_type: "branch", scope_value: value });
      });
      draft.departments.forEach((value) => {
        payload.push({
          module_code: moduleCode,
          scope_type: "department",
          scope_value: value,
        });
      });
    });

    return payload;
  };

  const handleSaveScopes = async () => {
    const employeeId = data?.employee_id || form.employee_id;
    if (!employeeId) {
      showErrorToast("Vui lòng chọn nhân viên trước khi cấu hình phạm vi dữ liệu.");
      return;
    }

    try {
      setLoading(true);
      await updateUserDataScopes(employeeId, buildScopePayload());
      showSuccessToast("Đã lưu phạm vi dữ liệu override.");
      onSaved?.();
    } catch (err) {
      console.error("Lỗi lưu user data scopes:", err);
      showErrorToast("Không thể lưu phạm vi dữ liệu override.");
    } finally {
      setLoading(false);
    }
  };

  const scopeBranchOptions = useMemo(
    () =>
      branches.map((branch) => ({
        value: branch.id,
        label: branch.name,
      })),
    [branches]
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((department) => ({
        value: department.id,
        label: department.branch_name
          ? `${department.branch_name} - ${department.name}`
          : department.name,
      })),
    [departments]
  );

  const rolesByDept = useMemo(() => {
    const map = new Map();
    for (const ur of usageRoles) {
      const key = ur.department_id || "__branch__";
      const title = ur.department_name
        ? `Bộ phận: ${ur.department_name}`
        : "Áp dụng cả chi nhánh (không gắn BP)";
      if (!map.has(key)) map.set(key, { title, items: [] });
      map.get(key).items.push(ur);
    }
    return [...map.values()];
  }, [usageRoles]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 24 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-4xl max-h-[90vh] bg-white rounded-[2rem] border border-gray-100 shadow-2xl overflow-hidden flex flex-col"
          >
            <div className="flex justify-between items-center px-6 py-5 border-b bg-white shrink-0">
              <div>
                <h2 className="text-xl font-black text-gray-800 uppercase tracking-tight">
                  {isEditing ? "Cập nhật người dùng / vai trò" : "Gán quyền sử dụng"}
                </h2>
                <p className="text-[11px] mt-1 text-gray-400 font-black uppercase tracking-widest">
                  Chi nhánh → Nhân viên → Quyền theo bộ phận
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2.5 rounded-2xl border border-gray-100 bg-gray-50 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-6 py-4 border-b bg-gray-50/60 flex flex-wrap gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setActiveTab("role")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                  activeTab === "role"
                    ? "bg-red-600 text-white shadow-lg shadow-red-100"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-red-200 hover:text-red-700"
                }`}
              >
                <ShieldCheck size={14} />
                Gán quyền sử dụng
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("scope")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                  activeTab === "scope"
                    ? "bg-red-600 text-white shadow-lg shadow-red-100"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-red-200 hover:text-red-700"
                }`}
              >
                <Database size={14} />
                Data scope override
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 flex-1 overflow-y-auto min-h-0">
              {activeTab === "role" ? (
                <>
                  {/* Bước 1–2 */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase mb-1.5 flex items-center gap-1">
                        <Building2 size={12} /> 1. Chi nhánh
                      </label>
                      <Select
                        styles={customSelectStyles}
                        options={branchOptions}
                        value={branchOptions.find((b) => b.value === branchId) || null}
                        onChange={(opt) => setBranchId(opt?.value || "")}
                        isDisabled={loading || isEditing}
                        placeholder="Chọn chi nhánh..."
                        menuPortalTarget={document.body}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase mb-1.5 flex items-center gap-1">
                        <Users size={12} /> 2. Nhân viên
                      </label>
                      <Select
                        styles={customSelectStyles}
                        options={employeeOptions}
                        value={
                          employeeOptions.find((e) => e.value === form.employee_id) || null
                        }
                        onChange={handleSelectEmployee}
                        isDisabled={loading || isEditing || !branchId}
                        placeholder={
                          !branchId ? "Chọn chi nhánh trước..." : "Chọn nhân viên..."
                        }
                        menuPortalTarget={document.body}
                        isClearable={!isEditing}
                      />
                      {branchId && !employeeOptions.length && (
                        <p className="text-xs text-amber-600 mt-1.5">
                          Chi nhánh này chưa có nhân viên đang làm việc.
                        </p>
                      )}
                    </div>
                  </div>

                  {form.employee_id && (
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/80 px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
                        <span>
                          <span className="text-gray-400 text-xs font-bold uppercase">BP:</span>{" "}
                          {selectedEmployee?.department_name || "—"}
                        </span>
                        <span>
                          <span className="text-gray-400 text-xs font-bold uppercase">CN:</span>{" "}
                          {selectedEmployee?.branch_name ||
                            branchOptions.find((b) => b.value === branchId)?.label ||
                            "—"}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        {roleLoading ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Loader2 size={14} className="animate-spin" /> Đang tải quyền hiện tại...
                          </span>
                        ) : currentRoles.length > 0 ? (
                          <>
                            <span className="font-medium">Đang gán:</span>{" "}
                            {currentRoles.map((r) => (
                              <span
                                key={`${r.id}-${r.usage_role_id || ""}`}
                                className="inline-block bg-white border border-gray-200 text-gray-700 px-2 py-1 rounded-lg mr-1 text-xs font-medium"
                              >
                                {r.usage_role_label || r.name}
                                {r.department_name ? ` · ${r.department_name}` : ""}
                              </span>
                            ))}
                          </>
                        ) : (
                          <span className="italic text-gray-500">Chưa được phân quyền</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bước 3: danh sách quyền */}
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                        <ShieldCheck size={12} /> 3. Chọn quyền sử dụng
                      </label>
                      {selectedEmployee?.department_name && (
                        <span className="text-[10px] font-bold uppercase text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                          Ưu tiên BP {selectedEmployee.department_name}
                        </span>
                      )}
                    </div>

                    {!form.employee_id ? (
                      <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-400">
                        Chọn chi nhánh và nhân viên để xem quyền phù hợp.
                      </div>
                    ) : usageRolesLoading ? (
                      <div className="flex items-center justify-center py-10 text-gray-500 gap-2">
                        <Loader2 className="animate-spin" size={18} />
                        Đang tải quyền theo chi nhánh / bộ phận...
                      </div>
                    ) : !usageRoles.length ? (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-4 text-sm">
                        <p className="font-bold mb-1">Chưa có quyền sử dụng phù hợp</p>
                        <p className="text-xs leading-relaxed">
                          Tạo quyền tại <strong>Cài đặt → Quyền sử dụng</strong> cho chi nhánh
                          này (và bộ phận của NV nếu cần), rồi quay lại gán.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs text-blue-900">
                          <Info className="h-4 w-4 shrink-0 mt-0.5" />
                          <span>
                            Chỉ hiện quyền của chi nhánh này: quyền gắn đúng bộ phận của NV hoặc
                            quyền áp dụng cả chi nhánh (không gắn BP). Chọn một quyền rồi bấm Lưu.
                          </span>
                        </div>

                        {rolesByDept.map((group) => (
                          <div key={group.title}>
                            <p className="text-[11px] font-bold uppercase text-gray-400 mb-2">
                              {group.title}
                            </p>
                            <div className="grid gap-2 sm:grid-cols-2">
                              {group.items.map((ur) => {
                                const active = usageRoleId === ur.id;
                                const matchDept =
                                  selectedEmployee?.department_id &&
                                  ur.department_id === selectedEmployee.department_id;
                                return (
                                  <button
                                    key={ur.id}
                                    type="button"
                                    onClick={() => handleSelectUsageRole(ur)}
                                    className={`text-left rounded-2xl border p-3 transition-all ${
                                      active
                                        ? "border-red-400 bg-red-50 ring-2 ring-red-200"
                                        : "border-gray-200 bg-white hover:border-red-200"
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="text-sm font-bold text-gray-800 truncate">
                                          {ur.label}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          Phạm vi: {scopeLabel(ur.scope_level)}
                                          {ur.department_name
                                            ? ` · ${ur.department_name}`
                                            : " · Cả chi nhánh"}
                                        </p>
                                      </div>
                                      {active ? (
                                        <span className="shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white">
                                          <Check size={14} />
                                        </span>
                                      ) : matchDept ? (
                                        <span className="shrink-0 text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                          Đúng BP
                                        </span>
                                      ) : null}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {!form.employee_id && !data?.employee_id ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-700 px-4 py-3 text-sm">
                      Vui lòng chọn nhân viên ở tab Gán quyền trước khi cấu hình phạm vi override.
                    </div>
                  ) : scopeLoading ? (
                    <div className="flex items-center justify-center py-10 text-gray-500 gap-2">
                      <Loader2 className="animate-spin" size={18} />
                      Đang tải phạm vi dữ liệu...
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-blue-100 bg-blue-50 text-blue-700 px-4 py-3 text-sm">
                        Override theo tài khoản được lưu theo <strong>employee_id</strong> — dùng khi
                        cần phạm vi khác với quyền sử dụng đã gán.
                      </div>

                      {scopeModules.map((moduleItem) => {
                        const draft = scopeDrafts[moduleItem.code] || createEmptyScopeDraft();
                        const selectedBranches = scopeBranchOptions.filter((item) =>
                          draft.branches.includes(item.value)
                        );
                        const selectedDepartments = departmentOptions.filter((item) =>
                          draft.departments.includes(item.value)
                        );

                        return (
                          <div
                            key={moduleItem.code}
                            className="rounded-2xl border border-gray-100 p-4 bg-gray-50/40"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                              <div>
                                <div className="font-bold text-gray-800">
                                  {moduleItem.name || moduleItem.code}
                                </div>
                                <div className="text-[11px] text-gray-400 uppercase font-bold">
                                  {moduleItem.code}
                                </div>
                              </div>
                              <div className="text-[11px] px-2 py-1 rounded-full bg-white border border-gray-200 text-gray-600 font-bold">
                                {summarizeScope(draft)}
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-3">
                              {[
                                { key: "all", label: "Toàn bộ" },
                                { key: "self", label: "Tự tạo" },
                                { key: "team", label: "Đội nhóm" },
                              ].map((scopeType) => (
                                <label
                                  key={scopeType.key}
                                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold ${
                                    draft[scopeType.key]
                                      ? "bg-red-50 text-red-700 border-red-200"
                                      : "bg-white text-gray-600 border-gray-200"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={draft[scopeType.key]}
                                    onChange={() => toggleScopeFlag(moduleItem.code, scopeType.key)}
                                    className="w-4 h-4 accent-slate-500"
                                  />
                                  {scopeType.label}
                                </label>
                              ))}
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <div>
                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">
                                  Chi nhánh
                                </label>
                                <Select
                                  isMulti
                                  isDisabled={draft.all}
                                  value={selectedBranches}
                                  onChange={(items) =>
                                    updateScopeMultiValue(moduleItem.code, "branches", items || [])
                                  }
                                  options={scopeBranchOptions}
                                  styles={customSelectStyles}
                                  menuPortalTarget={document.body}
                                  placeholder="Chọn chi nhánh..."
                                  closeMenuOnSelect={false}
                                />
                              </div>

                              <div>
                                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">
                                  Phòng ban
                                </label>
                                <Select
                                  isMulti
                                  isDisabled={draft.all}
                                  value={selectedDepartments}
                                  onChange={(items) =>
                                    updateScopeMultiValue(
                                      moduleItem.code,
                                      "departments",
                                      items || []
                                    )
                                  }
                                  options={departmentOptions}
                                  styles={customSelectStyles}
                                  menuPortalTarget={document.body}
                                  placeholder="Chọn phòng ban..."
                                  closeMenuOnSelect={false}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50/40 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all font-bold"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={activeTab === "scope" ? handleSaveScopes : handleSave}
                className="bg-red-600 text-white px-5 py-2.5 rounded-xl hover:bg-red-700 disabled:opacity-60 font-bold transition-all"
                disabled={
                  loading ||
                  (activeTab === "role" && (!form.employee_id || !form.role_id)) ||
                  (activeTab === "scope" && !(data?.employee_id || form.employee_id))
                }
              >
                {loading ? "Đang lưu..." : "Lưu"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AssignDrawer;
